import type { EventLogEntry } from "./app-events.ts";
import type { IglooBotApp } from "./app.ts";
import type { ExecApprovalRequest } from "./controllers/exec-approval.ts";
import type { GatewayEventFrame, GatewayHelloOk } from "./gateway.ts";
import type { Tab } from "./navigation.ts";
import type { UiSettings, AuthUser } from "./storage.ts";
import type { AgentsListResult, PresenceEntry, HealthSnapshot, StatusSummary } from "./types.ts";
import { CHAT_SESSIONS_ACTIVE_MINUTES, flushChatQueueForEvent } from "./app-chat.ts";
import {
  applySettings,
  loadCron,
  refreshActiveTab,
  setLastActiveSessionKey,
} from "./app-settings.ts";
import { handleAgentEvent, resetToolStream, type AgentEventPayload } from "./app-tool-stream.ts";
import { loadAgents } from "./controllers/agents.ts";
import { loadAssistantIdentity } from "./controllers/assistant-identity.ts";
import { loadChatHistory, loadChatCommands } from "./controllers/chat.ts";
import { handleChatEvent, type ChatEventPayload } from "./controllers/chat.ts";
import { loadDevices } from "./controllers/devices.ts";
import {
  addExecApproval,
  parseExecApprovalRequested,
  parseExecApprovalResolved,
  removeExecApproval,
} from "./controllers/exec-approval.ts";
import { loadCompanies, loadProjects } from "./controllers/igloobot.ts";
import { loadNodes } from "./controllers/nodes.ts";
import { handlePipelineProgressEvent } from "./controllers/pipelines.ts";
import { loadSessions } from "./controllers/sessions.ts";
import { GatewayBrowserClient } from "./gateway.ts";
import { loadAuth, clearAuth } from "./storage.ts";

import type { OverviewDataState } from "./controllers/overview.ts";

type GatewayHost = {
  settings: UiSettings;
  password: string;
  client: GatewayBrowserClient | null;
  connected: boolean;
  hello: GatewayHelloOk | null;
  lastError: string | null;
  onboarding?: boolean;
  eventLogBuffer: EventLogEntry[];
  eventLog: EventLogEntry[];
  tab: Tab;
  presenceEntries: PresenceEntry[];
  presenceError: string | null;
  presenceStatus: StatusSummary | null;
  agentsLoading: boolean;
  agentsList: AgentsListResult | null;
  agentsError: string | null;
  debugHealth: HealthSnapshot | null;
  assistantName: string;
  assistantAvatar: string | null;
  assistantAgentId: string | null;
  sessionKey: string;
  chatRunId: string | null;
  refreshSessionsAfterChat: Set<string>;
  execApprovalQueue: ExecApprovalRequest[];
  execApprovalError: string | null;
  // Auth state (RBAC)
  isAuthenticated: boolean;
  authToken: string | null;
  currentUser: AuthUser | null;
  // Overview dashboard data
  overviewData: OverviewDataState;
};

type SessionDefaultsSnapshot = {
  defaultAgentId?: string;
  mainKey?: string;
  mainSessionKey?: string;
  scope?: string;
};

function normalizeSessionKeyForDefaults(
  value: string | undefined,
  defaults: SessionDefaultsSnapshot,
): string {
  const raw = (value ?? "").trim();
  const mainSessionKey = defaults.mainSessionKey?.trim();
  if (!mainSessionKey) {
    return raw;
  }
  if (!raw) {
    return mainSessionKey;
  }
  const mainKey = defaults.mainKey?.trim() || "main";
  const defaultAgentId = defaults.defaultAgentId?.trim();
  const isAlias =
    raw === "main" ||
    raw === mainKey ||
    (defaultAgentId &&
      (raw === `agent:${defaultAgentId}:main` || raw === `agent:${defaultAgentId}:${mainKey}`));
  return isAlias ? mainSessionKey : raw;
}

function applySessionDefaults(host: GatewayHost, defaults?: SessionDefaultsSnapshot) {
  if (!defaults?.mainSessionKey) {
    return;
  }
  const resolvedSessionKey = normalizeSessionKeyForDefaults(host.sessionKey, defaults);
  const resolvedSettingsSessionKey = normalizeSessionKeyForDefaults(
    host.settings.sessionKey,
    defaults,
  );
  const resolvedLastActiveSessionKey = normalizeSessionKeyForDefaults(
    host.settings.lastActiveSessionKey,
    defaults,
  );
  const nextSessionKey = resolvedSessionKey || resolvedSettingsSessionKey || host.sessionKey;
  const nextSettings = {
    ...host.settings,
    sessionKey: resolvedSettingsSessionKey || nextSessionKey,
    lastActiveSessionKey: resolvedLastActiveSessionKey || nextSessionKey,
  };
  const shouldUpdateSettings =
    nextSettings.sessionKey !== host.settings.sessionKey ||
    nextSettings.lastActiveSessionKey !== host.settings.lastActiveSessionKey;
  if (nextSessionKey !== host.sessionKey) {
    host.sessionKey = nextSessionKey;
  }
  if (shouldUpdateSettings) {
    applySettings(host as unknown as Parameters<typeof applySettings>[0], nextSettings);
  }
}

/**
 * Validate the saved auth token on gateway (re)connect.
 * If the token is invalid/expired, clear auth state so the login screen shows.
 */
async function validateSavedAuth(host: GatewayHost) {
  const saved = loadAuth();
  if (!saved.authToken || !host.client || !host.connected) {
    return;
  }
  try {
    const result = (await host.client.request("igloobot.auth.validate", {
      token: saved.authToken,
    })) as { ok?: boolean; user?: AuthUser };
    if (result?.ok && result.user) {
      host.isAuthenticated = true;
      host.authToken = saved.authToken;
      host.currentUser = result.user as AuthUser;
    } else {
      // Token expired or invalid
      host.isAuthenticated = false;
      host.authToken = null;
      host.currentUser = null;
      clearAuth();
    }
  } catch {
    // If the RPC fails (e.g. plugin not loaded), clear auth
    host.isAuthenticated = false;
    host.authToken = null;
    host.currentUser = null;
    clearAuth();
  }
}

export function connectGateway(host: GatewayHost) {
  host.lastError = null;
  host.hello = null;
  host.connected = false;
  host.execApprovalQueue = [];
  host.execApprovalError = null;

  host.client?.stop();
  host.client = new GatewayBrowserClient({
    url: host.settings.gatewayUrl,
    token: host.settings.token.trim() ? host.settings.token : undefined,
    password: host.password.trim() ? host.password : undefined,
    clientName: "igloobot-control-ui",
    mode: "webchat",
    onHello: (hello) => {
      host.connected = true;
      host.lastError = null;
      host.hello = hello;
      applySnapshot(host, hello);
      // Sync overview data state
      host.overviewData.client = host.client;
      host.overviewData.connected = true;
      host.overviewData.gatewayConnected = true;
      // Reset orphaned chat run state from before disconnect.
      // Any in-flight run's final event was lost during the disconnect window.
      host.chatRunId = null;
      (host as unknown as { chatStream: string | null }).chatStream = null;
      (host as unknown as { chatStreamStartedAt: number | null }).chatStreamStartedAt = null;
      resetToolStream(host as unknown as Parameters<typeof resetToolStream>[0]);
      void loadAssistantIdentity(host as unknown as IglooBotApp);
      void loadAgents(host as unknown as IglooBotApp);
      void loadNodes(host as unknown as IglooBotApp, { quiet: true });
      void loadDevices(host as unknown as IglooBotApp, { quiet: true });
      // Eagerly load companies for the topbar company picker
      void loadCompanies(host as unknown as Parameters<typeof loadCompanies>[0]);
      // Eagerly load projects so the pinned-projects sidebar picker is populated
      void loadProjects(host as unknown as Parameters<typeof loadProjects>[0]);
      // Load tab visibility overrides for sidebar filtering
      void (async () => {
        try {
          const { loadTabVisibility } = await import("./controllers/tab-visibility.ts");
          await loadTabVisibility(host as unknown as IglooBotApp);
        } catch {
          // Non-critical — sidebar falls back to hardcoded TAB_ACCESS
        }
        try {
          const { loadRoleSettings } = await import("./controllers/role-settings.ts");
          await loadRoleSettings(host as unknown as IglooBotApp);
        } catch {
          // Non-critical — defaults to showing the picker
        }
      })();
      void refreshActiveTab(host as unknown as Parameters<typeof refreshActiveTab>[0]);
      // Pre-load slash-command list for autocomplete
      void loadChatCommands(host as unknown as IglooBotApp);
      // Validate saved auth token on reconnect
      void validateSavedAuth(host);
    },
    onClose: ({ code, reason }) => {
      host.connected = false;
      // Code 1012 = Service Restart (expected during config saves, don't show as error)
      if (code !== 1012) {
        host.lastError = `disconnected (${code}): ${reason || "no reason"}`;
      }
    },
    onEvent: (evt) => handleGatewayEvent(host, evt),
    onGap: ({ expected, received }) => {
      host.lastError = `event gap detected (expected seq ${expected}, got ${received}); refresh recommended`;
    },
  });
  // Carry the app-login token (survives reloads via loadAuth) so privileged
  // RPCs are authorized server-side.
  host.client.appAuthToken = host.authToken ?? null;
  host.client.start();
}

export function handleGatewayEvent(host: GatewayHost, evt: GatewayEventFrame) {
  try {
    handleGatewayEventUnsafe(host, evt);
  } catch (err) {
    console.error("[gateway] handleGatewayEvent error:", evt.event, err);
  }
}

function handleGatewayEventUnsafe(host: GatewayHost, evt: GatewayEventFrame) {
  host.eventLogBuffer = [
    { ts: Date.now(), event: evt.event, payload: evt.payload },
    ...host.eventLogBuffer,
  ].slice(0, 250);
  if (host.tab === "debug") {
    host.eventLog = host.eventLogBuffer;
  }

  if (evt.event === "agent") {
    if (host.onboarding) {
      return;
    }
    handleAgentEvent(
      host as unknown as Parameters<typeof handleAgentEvent>[0],
      evt.payload as AgentEventPayload | undefined,
    );
    return;
  }

  if (evt.event === "chat") {
    const payload = evt.payload as ChatEventPayload | undefined;
    if (payload?.sessionKey) {
      setLastActiveSessionKey(
        host as unknown as Parameters<typeof setLastActiveSessionKey>[0],
        payload.sessionKey,
      );
    }
    const state = handleChatEvent(host as unknown as IglooBotApp, payload);
    if (state === "final" || state === "error" || state === "aborted") {
      resetToolStream(host as unknown as Parameters<typeof resetToolStream>[0]);
      void flushChatQueueForEvent(host as unknown as Parameters<typeof flushChatQueueForEvent>[0]);
      const runId = payload?.runId;
      if (runId && host.refreshSessionsAfterChat.has(runId)) {
        host.refreshSessionsAfterChat.delete(runId);
        if (state === "final") {
          void loadSessions(host as unknown as IglooBotApp, {
            activeMinutes: CHAT_SESSIONS_ACTIVE_MINUTES,
          });
        }
      }
    }
    if (state === "final") {
      void loadChatHistory(host as unknown as IglooBotApp);
    }
    return;
  }

  if (evt.event === "presence") {
    const payload = evt.payload as { presence?: PresenceEntry[] } | undefined;
    if (payload?.presence && Array.isArray(payload.presence)) {
      host.presenceEntries = payload.presence;
      host.presenceError = null;
      host.presenceStatus = null;
    }
    return;
  }

  if (evt.event === "cron" && host.tab === "cron") {
    void loadCron(host as unknown as Parameters<typeof loadCron>[0]);
  }

  if (evt.event === "device.pair.requested" || evt.event === "device.pair.resolved") {
    void loadDevices(host as unknown as IglooBotApp, { quiet: true });
  }

  if (evt.event === "exec.approval.requested") {
    const entry = parseExecApprovalRequested(evt.payload);
    if (entry) {
      host.execApprovalQueue = addExecApproval(host.execApprovalQueue, entry);
      host.execApprovalError = null;
      const delay = Math.max(0, entry.expiresAtMs - Date.now() + 500);
      window.setTimeout(() => {
        host.execApprovalQueue = removeExecApproval(host.execApprovalQueue, entry.id);
      }, delay);
    }
    return;
  }

  if (evt.event === "exec.approval.resolved") {
    const resolved = parseExecApprovalResolved(evt.payload);
    if (resolved) {
      host.execApprovalQueue = removeExecApproval(host.execApprovalQueue, resolved.id);
    }
  }

  if (evt.event === "pipeline.progress") {
    handlePipelineProgressEvent(host as any, evt.payload);
  }

  if (evt.event === "chat.document") {
    const payload = evt.payload as
      | {
          runId?: string;
          sessionKey?: string;
          fileName?: string;
          status?: string;
          format?: string;
          chunks?: number;
          error?: string;
        }
      | undefined;
    if (payload?.fileName && payload.status) {
      const status = payload.status;
      const fileName = payload.fileName;
      if (status === "done") {
        // Append a system message to the chat showing document was processed
        const app = host as unknown as { chatMessages: unknown[]; sessionKey: string };
        if (payload.sessionKey === app.sessionKey) {
          app.chatMessages = [
            ...app.chatMessages,
            {
              role: "system",
              content: `Document "${fileName}" processed (${payload.format ?? "unknown"}, ${payload.chunks ?? 0} chunks). The agent can now search and read it.`,
              timestamp: Date.now(),
            },
          ];
        }
      } else if (status === "failed") {
        const app = host as unknown as { chatMessages: unknown[]; sessionKey: string };
        if (payload.sessionKey === app.sessionKey) {
          app.chatMessages = [
            ...app.chatMessages,
            {
              role: "system",
              content: `Failed to process document "${fileName}": ${payload.error ?? "unknown error"}`,
              timestamp: Date.now(),
            },
          ];
        }
      }
    }
  }
}

export function applySnapshot(host: GatewayHost, hello: GatewayHelloOk) {
  const snapshot = hello.snapshot as
    | {
        presence?: PresenceEntry[];
        health?: HealthSnapshot;
        sessionDefaults?: SessionDefaultsSnapshot;
      }
    | undefined;
  if (snapshot?.presence && Array.isArray(snapshot.presence)) {
    host.presenceEntries = snapshot.presence;
  }
  if (snapshot?.health) {
    host.debugHealth = snapshot.health;
  }
  if (snapshot?.sessionDefaults) {
    applySessionDefaults(host, snapshot.sessionDefaults);
  }
}
